/**
 * Widget de NPS — slide-in panel fixo no canto inferior direito.
 * Exibe em 2 etapas: (1) pontuação 0-10, (2) comentário opcional.
 * Auto-dispensa após 30 segundos.
 */

import React, { useEffect, useState, useCallback } from 'react'
import {
  Box,
  Button,
  IconButton,
  Paper,
  Slide,
  Stack,
  TextField,
  Typography
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'

type Props = {
  onClose: () => void
  onSubmit: (score: number, feedback: string) => Promise<void>
}

export default function NPSWidget({ onClose, onSubmit }: Props) {
  const [step, setStep] = useState<1 | 2>(1)
  const [score, setScore] = useState<number | null>(null)
  const [feedback, setFeedback] = useState('')
  const [submitting, setSubmitting] = useState(false)

  // Auto-dismiss após 30s
  useEffect(() => {
    const timer = setTimeout(onClose, 30000)
    return () => clearTimeout(timer)
  }, [onClose])

  const handleScoreSelect = useCallback((value: number) => {
    setScore(value)
    setStep(2)
  }, [])

  const handleSubmit = useCallback(async () => {
    if (score === null) return
    setSubmitting(true)
    try {
      await onSubmit(score, feedback)
    } finally {
      setSubmitting(false)
    }
  }, [score, feedback, onSubmit])

  return (
    <Slide in direction="up" mountOnEnter unmountOnExit>
      <Paper
        elevation={8}
        sx={{
          position: 'fixed',
          bottom: { xs: 88, md: 24 },
          right: 24,
          width: { xs: 'calc(100vw - 48px)', sm: 380 },
          maxWidth: 380,
          zIndex: 1300,
          borderRadius: 3,
          overflow: 'hidden'
        }}
      >
        {/* Header */}
        <Box
          sx={{
            bgcolor: 'primary.main',
            px: 2.5,
            py: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <Typography variant="subtitle2" fontWeight={600} color="white">
            {step === 1
              ? 'Como está sua experiência?'
              : 'Quer nos contar mais?'}
          </Typography>
          <IconButton size="small" onClick={onClose} sx={{ color: 'white', p: 0.5 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Box sx={{ px: 2.5, py: 2 }}>
          {step === 1 ? (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                De 0 a 10, o quanto você recomendaria esta ferramenta a um colega?
              </Typography>

              {/* Grade 0-10 */}
              <Box
                sx={{
                  display: 'flex',
                  gap: 0.5,
                  flexWrap: 'wrap',
                  justifyContent: 'center',
                  mb: 1
                }}
              >
                {Array.from({ length: 11 }, (_, i) => (
                  <Box
                    key={i}
                    onClick={() => handleScoreSelect(i)}
                    sx={{
                      width: 34,
                      height: 34,
                      borderRadius: 1.5,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      fontWeight: 600,
                      fontSize: '0.85rem',
                      bgcolor: score === i ? 'primary.main' : '#f5f5f5',
                      color: score === i ? 'white' : 'text.primary',
                      border: '1px solid',
                      borderColor: score === i ? 'primary.main' : '#e0e0e0',
                      userSelect: 'none',
                      transition: 'all 0.12s ease',
                      '&:hover': {
                        bgcolor: 'primary.light',
                        color: 'white',
                        borderColor: 'primary.light'
                      }
                    }}
                  >
                    {i}
                  </Box>
                ))}
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  Muito improvável
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Muito provável
                </Typography>
              </Box>

              <Stack direction="row" justifyContent="flex-end">
                <Button
                  size="small"
                  onClick={onClose}
                  sx={{ color: 'text.secondary', textTransform: 'none' }}
                >
                  Agora não
                </Button>
              </Stack>
            </>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Obrigado! Quer compartilhar algum comentário? (opcional)
              </Typography>

              <TextField
                multiline
                rows={3}
                fullWidth
                placeholder="O que poderia ser melhorado?"
                value={feedback}
                onChange={e => setFeedback(e.target.value)}
                size="small"
                sx={{ mb: 2 }}
              />

              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Button
                  size="small"
                  onClick={onClose}
                  sx={{ color: 'text.secondary', textTransform: 'none' }}
                >
                  Pular
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleSubmit}
                  disabled={submitting}
                  sx={{ textTransform: 'none' }}
                >
                  Enviar
                </Button>
              </Stack>
            </>
          )}
        </Box>
      </Paper>
    </Slide>
  )
}
